import CIcon from "@coreui/icons-react";
import { cilClock } from "@coreui/icons";
import { TRADINGVIEW_SCRIPTS_ADD_ON_TYPE, useAddOns } from "../../../api/hooks/useAddOns";
import { useTradingViewAccount } from "../../../api/hooks/useTradingViewAccount";
import { useTradingViewScripts } from "../../../api/hooks/useTradingViewScripts";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";
import { addOnDetailsPath, routePaths } from "../../../routes/route-paths";
import { StrategyColumnLink } from "./StrategyColumnLink";
import { TradingViewMark } from "./TradingViewMark";

interface TradingViewScriptLinkProps {
  strategy: TradingStrategyDto;
  /** Where adding a username comes back to. */
  returnTo: string;
}

/**
 * Where the customer stands with this strategy's TradingView script. The script is invite-only, so only an invited
 * customer is sent to TradingView; anyone outside the club goes to the club's page while it is on sale.
 */
export function TradingViewScriptLink({ strategy, returnTo }: TradingViewScriptLinkProps) {
  const addOns = useAddOns();
  // Only live add-ons and the customer's own are listed, so one listed and not held is on sale.
  const club = addOns.data?.find((addOn) => addOn.type === TRADINGVIEW_SCRIPTS_ADD_ON_TYPE);
  const holdsClub = club !== undefined && club.held !== null;
  const account = useTradingViewAccount();
  const scripts = useTradingViewScripts({ enabled: holdsClub });

  if (!club) {
    return null;
  }

  const mark = <TradingViewMark size={16} />;

  if (!holdsClub) {
    return (
      <StrategyColumnLink icon={mark} to={addOnDetailsPath(club.id)}>
        Get this script on TradingView
      </StrategyColumnLink>
    );
  }

  // Nothing until both are known, so an invited strategy never flashes as on its way.
  if (!account.data || !scripts.data) {
    return null;
  }

  const invite = scripts.data.find((script) => script.tradingStrategyId === strategy.id);

  if (invite) {
    return strategy.publicationUrl ? (
      <StrategyColumnLink icon={mark} href={strategy.publicationUrl} meta={invite.tradingViewUsername}>
        Open on TradingView
      </StrategyColumnLink>
    ) : (
      <StrategyColumnLink icon={mark} muted meta={invite.tradingViewUsername}>
        On TradingView
      </StrategyColumnLink>
    );
  }

  const username = account.data.username || null;

  return username ? (
    <StrategyColumnLink icon={<CIcon icon={cilClock} className="text-warning" />} muted meta={username}>
      TradingView invite on its way
    </StrategyColumnLink>
  ) : (
    <StrategyColumnLink icon={mark} to={routePaths.profileTradingView} state={{ returnTo }}>
      Add your TradingView username
    </StrategyColumnLink>
  );
}
